/**
 * SecondsInput Component
 *
 * A modal-based MM:SS duration input for tummy time tracking.
 * Stores value as total seconds (string). Displays as M:SS format.
 */

import { useState } from 'react';

interface SecondsPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSeconds: number;
  onSave: (totalSeconds: number) => void;
  title?: string;
}

function formatSeconds(totalSeconds: number): string {
  if (isNaN(totalSeconds) || totalSeconds < 0) return '0:00';
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function SecondsPickerModal({ isOpen, onClose, initialSeconds, onSave, title = 'Set Duration' }: SecondsPickerModalProps) {
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  // Initialize from total seconds
  useState(() => {
    if (isOpen) {
      setMinutes(Math.floor(initialSeconds / 60));
      setSeconds(initialSeconds % 60);
    }
  });

  // Re-init when modal opens
  const [lastOpen, setLastOpen] = useState(false);
  if (isOpen && !lastOpen) {
    setLastOpen(true);
    setMinutes(Math.floor(initialSeconds / 60));
    setSeconds(initialSeconds % 60);
  } else if (!isOpen && lastOpen) {
    setLastOpen(false);
  }

  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  const handleSave = () => {
    onSave(minutes * 60 + seconds);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 text-center">{title}</h2>

        <div className="flex gap-6 justify-center mb-8">
          {/* Minutes */}
          <div className="flex flex-col items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Minutes</label>
            <button type="button" onClick={() => setMinutes(v => clamp(v + 1, 0, 99))}
              className="w-14 h-14 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-2xl font-bold mb-2 active:scale-95 transition-transform">+</button>
            <input type="number" value={minutes}
              onChange={(e) => setMinutes(clamp(parseInt(e.target.value) || 0, 0, 99))}
              className="w-20 h-16 text-center text-3xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-teal-500"
              min="0" max="99" />
            <button type="button" onClick={() => setMinutes(v => clamp(v - 1, 0, 99))}
              className="w-14 h-14 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-2xl font-bold mt-2 active:scale-95 transition-transform">−</button>
          </div>

          <div className="flex items-center text-4xl font-bold text-gray-400 dark:text-gray-500">:</div>

          {/* Seconds */}
          <div className="flex flex-col items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Seconds</label>
            <button type="button" onClick={() => setSeconds(v => clamp(v + 1, 0, 59))}
              className="w-14 h-14 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-2xl font-bold mb-2 active:scale-95 transition-transform">+</button>
            <input type="number" value={seconds.toString().padStart(2, '0')}
              onChange={(e) => setSeconds(clamp(parseInt(e.target.value) || 0, 0, 59))}
              className="w-20 h-16 text-center text-3xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-teal-500"
              min="0" max="59" />
            <button type="button" onClick={() => setSeconds(v => clamp(v - 1, 0, 59))}
              className="w-14 h-14 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-2xl font-bold mt-2 active:scale-95 transition-transform">−</button>
          </div>
        </div>

        {/* Quick increments */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">Quick add:</p>
          <div className="flex gap-2 justify-center">
            {[15, 30, 60].map(s => (
              <button key={s} type="button"
                onClick={() => {
                  const total = minutes * 60 + seconds + s;
                  setMinutes(Math.floor(total / 60));
                  setSeconds(total % 60);
                }}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300">
                +{s}s
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave}
            className="flex-1 px-4 py-3 text-base font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-lg transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

interface SecondsInputProps {
  value: string; // total seconds as string
  onChange: (seconds: string) => void;
  label: string;
  placeholder?: string;
}

export default function SecondsInput({ value, onChange, label, placeholder = '0:00' }: SecondsInputProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const totalSeconds = parseInt(value, 10) || 0;
  const displayValue = value ? formatSeconds(totalSeconds) : placeholder;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      <button type="button" onClick={() => setIsModalOpen(true)}
        className="w-full px-4 py-2 text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors"
        aria-label={`${label}: ${displayValue}`}>
        <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
          {displayValue}
        </span>
      </button>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Click to set duration (MM:SS)</p>
      <SecondsPickerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialSeconds={totalSeconds}
        onSave={(s) => onChange(s.toString())}
        title={label}
      />
    </div>
  );
}
